import React from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Chip,
  Divider,
} from '@nextui-org/react';
import { RetailTargetSku } from 'common/src/utils/types';

export interface ScrapeResult {
  sku_id: number;
  retailer: string;
  country: string;
  status: 'ok' | 'no_product' | 'error';
  message?: string;
  extracted?: {
    product_name?: string | null;
    price_local?: number | null;
    package_size_text?: string | null;
    package_size_value?: number | null;
    package_size_unit?: string | null;
    origin_country?: string | null;
    on_promo?: boolean;
    notes?: string;
  };
  computed?: {
    weight_kg: number | null;
    weight_source: string | null;
    fx_rate_used: number | null;
    price_usd_per_kg: number | null;
    markdown_length: number;
  };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  sku: RetailTargetSku | null;
  result: ScrapeResult | null;
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className='flex justify-between items-start gap-3 py-1'>
    <span className='text-sm text-default-500'>{label}</span>
    <span className='text-sm font-medium text-right break-words'>
      {value ?? <span className='text-default-300'>—</span>}
    </span>
  </div>
);

const TestScrapeModal: React.FC<Props> = ({ isOpen, onClose, sku, result }) => {
  if (!sku) return null;

  const isOk = result?.status === 'ok';
  const isNoProduct = result?.status === 'no_product';
  const isError = result?.status === 'error';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='2xl' scrollBehavior='inside'>
      <ModalContent>
        <ModalHeader className='flex flex-col gap-1'>
          <div className='flex items-center gap-2'>
            <span>Test scrape</span>
            <Chip size='sm' variant='flat' color='warning'>
              Dry-run
            </Chip>
          </div>
          <p className='text-sm font-normal text-default-500'>
            {sku.retailer} · {sku.country} · {sku.tier ?? '—'}
          </p>
          <a
            href={sku.url}
            target='_blank'
            rel='noreferrer'
            className='text-xs text-primary-500 underline truncate max-w-full'
          >
            {sku.url}
          </a>
        </ModalHeader>

        <ModalBody>
          <div className='p-3 bg-default-50 border border-default-200 rounded'>
            <p className='text-sm text-default-600'>
              Este test solo previsualiza la extracción. No guarda puntos en la
              serie de tiempo.
            </p>
          </div>

          {!result && (
            <p className='text-default-500'>Sin resultado todavía.</p>
          )}

          {isError && (
            <div className='p-3 bg-danger-50 dark:bg-danger-950 border border-danger-200 rounded'>
              <p className='text-sm font-medium text-danger-700'>
                ✗ Falló el scrape
              </p>
              <p className='text-xs text-danger-600 mt-1'>
                {result?.message ?? 'Sin detalles'}
              </p>
            </div>
          )}

          {isNoProduct && (
            <div className='p-3 bg-warning-50 dark:bg-warning-950 border border-warning-200 rounded'>
              <p className='text-sm font-medium text-warning-700'>
                ⚠ No se encontró el producto esperado
              </p>
              <p className='text-xs text-warning-600 mt-1'>
                {result?.extracted?.notes?.slice(0, 300) ?? result?.message}
              </p>
            </div>
          )}

          {isOk && result?.extracted && result?.computed && (
            <div className='space-y-3'>
              <div>
                <p className='text-xs font-semibold uppercase text-default-500 mb-1'>
                  Producto extraído
                </p>
                <Row label='Nombre' value={result.extracted.product_name} />
                <Row
                  label='Origen'
                  value={
                    result.extracted.origin_country ?? (
                      <span className='text-default-400 italic'>
                        no expuesto
                      </span>
                    )
                  }
                />
                <Row
                  label='En promoción'
                  value={result.extracted.on_promo ? '🔖 Sí' : 'No'}
                />
              </div>

              <Divider />

              <div>
                <p className='text-xs font-semibold uppercase text-default-500 mb-1'>
                  Precio
                </p>
                <Row
                  label='Precio local'
                  value={`${result.extracted.price_local} ${sku.currency}`}
                />
                <Row
                  label='Package en página'
                  value={result.extracted.package_size_text}
                />
                <Row
                  label='Valor / unidad'
                  value={
                    <>
                      {result.extracted.package_size_value}{' '}
                      <Chip size='sm' variant='flat'>
                        {result.extracted.package_size_unit ?? '—'}
                      </Chip>
                    </>
                  }
                />
                <Row
                  label='Peso (kg)'
                  value={
                    <>
                      {result.computed.weight_kg} kg{' '}
                      <Chip
                        size='sm'
                        variant='flat'
                        color={
                          result.computed.weight_source === 'explicit'
                            ? 'success'
                            : 'warning'
                        }
                      >
                        {result.computed.weight_source}
                      </Chip>
                    </>
                  }
                />
                <Row
                  label='FX rate (USD→local)'
                  value={result.computed.fx_rate_used}
                />
                <Row
                  label='Precio USD/kg ⭐'
                  value={
                    <span className='text-success-600 font-bold'>
                      ${result.computed.price_usd_per_kg} /kg
                    </span>
                  }
                />
              </div>

              {result.extracted.notes && (
                <>
                  <Divider />
                  <div>
                    <p className='text-xs font-semibold uppercase text-default-500 mb-1'>
                      Notas del extractor (LLM)
                    </p>
                    <p className='text-xs text-default-600 italic bg-default-50 dark:bg-default-100 p-2 rounded max-h-32 overflow-y-auto'>
                      {result.extracted.notes}
                    </p>
                  </div>
                </>
              )}

              <p className='text-xs text-default-400'>
                Markdown bruto:{' '}
                {result.computed.markdown_length.toLocaleString()} chars
              </p>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant='light' onClick={onClose}>
            Cerrar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TestScrapeModal;
